import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { camelize } from '@ember/string';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, SourceInfo } from 'webapp-types';

import eq from 'webapp/helpers/eq';
import joinClasses from 'webapp/helpers/join-classes';
import CommonHtmlContainerComponent from 'webapp/components/common/html-container';
import CommonSkeletonBarLoaderComponent from 'webapp/components/common/skeleton-bar-loader';
import MetricLibraryMetricDimensionsComponent from 'webapp/components/metric-library/metric/dimensions';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import VerificationToggleMetricComponent from 'webapp/components/verification/toggle/metric';

import Metric from 'webapp/models-custom/metric';

import type {
	MetricExtraInfoKey,
	MetrickeyExtraInfo,
} from 'webapp/services/model-manager/source-types';

export const enum MetricTabId {
	ABOUT = 'what_is',
	TECH_DETAILS = 'tech_details',
	NOTES = 'notes',
	DIMENSIONS = 'dimensions',
}

interface MetricTab {
	id: MetricTabId;
	title: string;
	count?: number;
}

export default class MetricLibraryPopupsPopupMetricExtraInfo extends PopupInnerModalComponent<PopupData.MetricLibrary.MetricBasicInfo> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked selectedTab!: MetricTabId; // set in setInitialTab
	// The About, Technical details and Notes texts are on their way; the tabs stay clickable meanwhile.
	@tracked isExtraInfoLoading = false;
	// Once the user picks a tab, the texts arriving don't move them off it.
	private hasSelectedTab = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.sourceType && this.shortMetrickey) {
			// Known from an earlier open: no loading state.
			this.isExtraInfoLoading = !this.metricExtraInfo;
			void this.mmSourceTypesService
				.requestMetricExtraInfoByKey(this.sourceType.key, this.shortMetrickey)
				.then(() => {
					if (this.isDestroying || this.isDestroyed) {
						return;
					}

					this.isExtraInfoLoading = false;

					// The user's pick stays, unless it was About and the metric turned out to have none.
					if (
						!this.hasSelectedTab ||
						!this.tabs.some(({ id }) => id === this.selectedTab)
					) {
						this.setInitialTab();
					}

					// Extra info loads asynchronously and changes the modal's
					// content/height. Defer to the next runloop so the new
					// content is rendered before we measure and reposition.
					next(() => {
						if (!this.isDestroying && !this.isDestroyed) {
							this.scheduleReposition();
						}
					});
				});
		}

		this.setInitialTab();
	}

	get isMetric(): boolean {
		return this.popupData.model instanceof Metric;
	}

	get source(): SourceInfo | null {
		if (this.isMetric) {
			const model = this.popupData.model as Metric;
			return {
				avatar: model.sourceAvatar,
				label: model.sourceName,
			};
		}

		return null;
	}

	get sourceType() {
		if (this.isMetric) {
			const model = this.popupData.model as Metric;
			return model.sourceType;
		}

		return null;
	}

	get shortMetrickey() {
		let metrickey = '';

		if (this.isMetric) {
			const model = this.popupData.model as Metric;

			// to achieve uniqueness, generic keys have a prefix with @ at the start (e.g. GoogleAnalytics4@sessions)
			// when searching for match for a metrickey, we need to ignore the prefix (e.g. use only "sessions")
			const [prefix, postfix] = this.mmMetricsService.getKeyParts(model.key);
			metrickey = (postfix || prefix)!;
		}

		return metrickey;
	}

	get name() {
		return this.popupData.model.name;
	}

	get description() {
		let description = '';
		if (this.isMetric) {
			const model = this.popupData.model as Metric;
			description = model.description;
		}

		return description;
	}

	get metricExtraInfo(): MetrickeyExtraInfo | null {
		return this.sourceType
			? this.mmSourceTypesService.metrickeysExtraInfoByKey[
					`${this.sourceType.key}@${this.shortMetrickey}`
				]
			: null;
	}

	get metricAsMetric() {
		return this.popupData.model as Metric;
	}

	get metricHasDimensions() {
		if (this.isMetric) {
			return this.metricAsMetric?.allowDimensions;
		}
		return false;
	}

	get tabs(): MetricTab[] {
		const candidates: MetricTab[] = [
			{
				id: MetricTabId.ABOUT,
				title: 'ABOUT',
			},
			{
				id: MetricTabId.TECH_DETAILS,
				title: 'TECHNICAL DETAILS',
			},
			{
				id: MetricTabId.NOTES,
				title: 'NOTES',
			},
		];
		const tabs: MetricTab[] = [];

		candidates.forEach((tab) => {
			// About shows while the texts load, so it can be selected first; it goes if it turns out to have none.
			const isLoadingAbout = this.isExtraInfoLoading && tab.id === MetricTabId.ABOUT;

			if (isLoadingAbout || this.metricExtraInfo?.[camelize(tab.id) as MetricExtraInfoKey]) {
				tabs.push(tab);
			}
		});

		if (this.metricHasDimensions) {
			tabs.push({
				id: MetricTabId.DIMENSIONS,
				title: 'AVAILABLE DIMENSIONS',
				count: this.metricAsMetric.dimensions.length,
			});
		}

		return tabs;
	}

	get isDimensionsTab() {
		return this.selectedTab === MetricTabId.DIMENSIONS;
	}

	get extraInfoTabContent() {
		return this.getExtraInfoTabContent(this.selectedTab);
	}

	get isExtraInfoTabLoading() {
		return this.isExtraInfoLoading && !this.isDimensionsTab;
	}

	get showContent() {
		return this.isDimensionsTab || this.isExtraInfoTabLoading || !!this.extraInfoTabContent;
	}

	@action
	selectTab(tab: MetricTabId): void {
		this.hasSelectedTab = true;
		this.selectedTab = tab;
		this.scheduleReposition();
	}

	// The opener's tab, else About; Available dimensions when the texts arrived without an About.
	private setInitialTab() {
		if (this.popupData.initialTab) {
			this.selectedTab = this.popupData.initialTab;
		} else if (
			!this.isExtraInfoLoading &&
			!this.getExtraInfoTabContent(MetricTabId.ABOUT) &&
			this.metricHasDimensions
		) {
			this.selectedTab = MetricTabId.DIMENSIONS;
		} else {
			this.selectedTab = MetricTabId.ABOUT;
		}
	}

	private getExtraInfoTabContent(tabId: MetricTabId) {
		return this.metricExtraInfo?.[camelize(tabId) as MetricExtraInfoKey];
	}
	<template>
		<div class="header rich-header-content">
			<div class="header__title">
				<div class="metric__name">
					{{this.name}}
				</div>
				<img class="source__avatar" src={{this.source.avatar}} alt={{this.source.label}} />
				<div class="source__name">{{this.source.label}}</div>
			</div>
			<div class="header__description">
				{{this.description}}
			</div>
			<VerificationToggleMetricComponent @metric={{this.metricAsMetric}} />
			<div class="header__tabs">
				{{#each this.tabs as |tab|}}
					<div
						class={{joinClasses
							(array
								"navigation-button"
								(if (eq tab.id this.selectedTab) "navigation-button--selected")
							)
						}}
						role="button"
						{{on "click" (fn this.selectTab tab.id)}}
					>
						<a class="navigation-button-link">{{tab.title}}</a>
						{{#if tab.count}}
							<small>{{tab.count}}</small>
						{{/if}}
					</div>
				{{/each}}
			</div>
		</div>

		{{#if this.showContent}}
			<div class="content">
				{{#if this.isDimensionsTab}}
					<MetricLibraryMetricDimensionsComponent @model={{this.metricAsMetric}} />
				{{else if this.isExtraInfoTabLoading}}
					<CommonSkeletonBarLoaderComponent />
					<CommonSkeletonBarLoaderComponent />
					<CommonSkeletonBarLoaderComponent />
				{{else if this.extraInfoTabContent}}
					<CommonHtmlContainerComponent @content={{this.extraInfoTabContent}} />
				{{/if}}
			</div>
		{{/if}}
	</template>
}
